// force rebuild
import React, { useEffect, useLayoutEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate, useParams } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "./lib/auth";
import { startKeepalive } from "./lib/api";
import Nav from "./components/Nav";
import Footer from "./components/Footer";
import InstallPrompt from "./components/InstallPrompt";
import GlobalSchemas from "./components/GlobalSchemas";
import Landing from "./pages/Landing";
import Search from "./pages/Search";
import ParkDetail from "./pages/ParkDetail";
import { Login, Signup } from "./pages/Auth";
import PrivateParkDetail from "./pages/PrivateParkDetail";
import PrivateParkHost from "./pages/PrivateParkHost";
import PrivateParksIndex from "./pages/PrivateParksIndex";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import AuthCallback from "./pages/AuthCallback";
import Onboarding from "./pages/Onboarding";
import Profile from "./pages/Profile";
import Guides from "./pages/Guides";
import GuideDetail from "./pages/GuideDetail";
import SuggestPlace from "./pages/SuggestPlace";
import SubmittedPlaceDetail from "./pages/SubmittedPlaceDetail";
import AdminSubmissions from "./pages/AdminSubmissions";
import AdminPrivateParkOnboard from "./pages/AdminPrivateParkOnboard";
import LegalPage from "./pages/LegalPage";

/**
 * Forwards legacy /blog/:slug URLs to /guides/:slug. Used for the
 * Blog → Guides merge so old inbound links keep working — React-router
 * `replace`-style navigation acts like a client-side 301 from the
 * user's POV. The prerendered HTML at /blog/{slug}/index.html also
 * carries a `<meta http-equiv="refresh">` + canonical-tag-to-guides
 * combo so crawlers/bots see a server-style redirect signal in the raw
 * HTML before JS runs.
 */
const BlogToGuideRedirect = () => {
  const { slug } = useParams();
  return <Navigate to={slug ? `/guides/${slug}` : "/guides"} replace />;
};

// /dog-beaches is the real beach address; forwards old /dog-friendly-beaches URLs.
const LegacyBeachRedirect = () => {
  const { "*": rest } = useParams();
  const target = rest ? `/dog-beaches/${rest}` : "/dog-beaches";
  useLayoutEffect(() => {
    const prev = [];
    const set = (sel, tag, attrs) => {
      let el = document.querySelector(sel);
      const created = !el;
      if (!el) {
        el = document.createElement(tag);
        document.head.appendChild(el);
      }
      prev.push({ el, created, old: Object.keys(attrs).map((k) => [k, el.getAttribute(k)]) });
      Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
    };
    set('meta[name="robots"]', "meta", { name: "robots", content: "noindex, follow" });
    set('link[rel="canonical"]', "link", { rel: "canonical", href: `https://pawground.app${target}` });
    return () => prev.forEach(({ el, created, old }) => {
      if (created) el.remove();
      else old.forEach(([k, v]) => (v === null ? el.removeAttribute(k) : el.setAttribute(k, v)));
    });
  }, [target]);
  return <Navigate to={target} replace />;
};
import CityPage from "./pages/CityPage";

const AppRouter = () => {
  const location = useLocation();

  // Google Analytics SPA page_view — fire on every route change.
  useEffect(() => {
    if (typeof window !== "undefined" && typeof window.gtag === "function") {
      window.gtag("event", "page_view", {
        page_path: location.pathname + location.search,
        page_location: window.location.href,
        page_title: document.title,
      });
    }
  }, [location.pathname, location.search]);

  // CRITICAL: check URL fragment synchronously during render (not in useEffect)
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  return (
    <>
      <GlobalSchemas />
      <Nav />
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/search" element={<Search />} />
        <Route path="/park/:id" element={<ParkDetail />} />
        {/* Private park listings — must come before the catch-all
            /:categorySlug/:citySlug routes so the slug isn't consumed
            as a city. Today only Jennifer's St. Pete yard; data is
            statically registered in src/data/privateParks.js. The
            /host and bare /private-parks routes MUST come before
            /:slug so they aren't matched as a listing slug. */}
        <Route path="/private-parks" element={<PrivateParksIndex />} />
        <Route path="/private-parks/host" element={<PrivateParkHost />} />
        <Route path="/private-parks/:slug" element={<PrivateParkDetail />} />
        <Route path="/login" element={<Login />} />
        <Route path="/signup" element={<Signup />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="/onboarding" element={<Onboarding />} />
        <Route path="/profile" element={<Profile />} />
        <Route path="/privacy" element={<LegalPage key="privacy" pageKey="privacy" />} />
        <Route path="/terms" element={<LegalPage key="terms" pageKey="terms" />} />
        {/* Community-submitted places. Both routes MUST come BEFORE
            the catch-all `/:citySlug` route below so React-Router
            matches the static `/suggest` + `/place` prefixes first.
            `/place/:slug` resolves to a UGC place detail page that
            re-uses ParkDetail-shaped data fetched via the unified
            `/api/places/{id}` endpoint (id = `sp_<slug>`). */}
        <Route path="/suggest" element={<SuggestPlace />} />
        <Route path="/place/:slug" element={<SubmittedPlaceDetail />} />
        {/* Admin moderation dashboard. Token-gated client-side; the
            backend independently enforces X-Admin-Token on every
            mutation. Noindex'd so it never appears in search. */}
        <Route path="/admin/submissions" element={<AdminSubmissions />} />
        {/* Admin: Private Park host onboarding form. Generates the
            JSON snippet to paste into src/data/privateParks.js — no
            backend write, output is copy-paste only. Gated by the
            same X-Admin-Token sessionStorage key as /admin/submissions.
            Noindex'd. */}
        <Route path="/admin/private-parks/onboard" element={<AdminPrivateParkOnboard />} />
        {/* Blog → Guides merge: /blog and /blog/:slug forward to the
            matching /guides path. Kept as React routes so client-side
            navigation works; the prerender script ALSO emits real HTML
            files at /blog/{slug}/index.html with `<meta http-equiv="refresh">`
            so Googlebot sees a redirect signal in raw HTML before JS. */}
        <Route path="/blog" element={<Navigate to="/guides" replace />} />
        <Route path="/blog/:slug" element={<BlogToGuideRedirect />} />
        <Route path="/guides" element={<Guides />} />
        {/* Retired guide slugs → redirect to their replacements. Keep
            this list aligned with REMOVED_SLUGS in backend/seed_guides.py
            so old inbound links never 404. Each entry must appear
            BEFORE the catch-all /:slug route below. */}
        <Route
          path="/guides/best-off-leash-dog-parks-tampa"
          element={<Navigate to="/guides/best-dog-parks-tampa-bay" replace />}
        />
        <Route path="/guides/:slug" element={<GuideDetail />} />
        {/* Category hub URLs — `/dog-parks`, `/dog-beaches`, etc. Render the
            same Search component with the category pre-selected via the
            `categoryUrlSlug` prop. Registered explicitly (not via /:slug)
            so each gets its own statically-prerendered HTML shell with
            unique SEO head tags and they take precedence over the dynamic
            /:citySlug route. */}
        <Route path="/dog-parks" element={<Search categoryUrlSlug="dog-parks" />} />
        <Route path="/dog-beaches" element={<Search categoryUrlSlug="dog-beaches" />} />
        <Route path="/dog-friendly-beaches/*" element={<LegacyBeachRedirect />} />
        <Route path="/dog-friendly-restaurants" element={<Search categoryUrlSlug="dog-friendly-restaurants" />} />
        <Route path="/vets" element={<Search categoryUrlSlug="vets" />} />
        <Route path="/groomers" element={<Search categoryUrlSlug="groomers" />} />
        <Route path="/boarding" element={<Search categoryUrlSlug="boarding" />} />
        <Route path="/trainers" element={<Search categoryUrlSlug="trainers" />} />
        <Route path="/pet-stores" element={<Search categoryUrlSlug="pet-stores" />} />
        <Route path="/trails" element={<Search categoryUrlSlug="trails" />} />
        <Route path="/fl/:slug" element={<CityPage stateCode="fl" />} />
        <Route path="/fl/:slug/:category" element={<CityPage stateCode="fl" />} />
        <Route path="/pr/:slug" element={<CityPage stateCode="pr" />} />
        <Route path="/pr/:slug/:category" element={<CityPage stateCode="pr" />} />
        {/* Fix #2 canonical place-detail URL:
            /{categorySlug}/{citySlug}/{placeSlug}
              e.g. /vets/tampa-fl/river-city-veterinary-a7f9c2
            Must come BEFORE the 2-segment {categorySlug}/{citySlug} and
            1-segment {citySlug} catch-alls below so the 3-segment path
            is matched first. ParkDetail internally resolves the last
            slug via /api/parks/{slug} (backend accepts slug OR id). */}
        <Route path="/:categorySlug/:citySlug/:placeSlug" element={<ParkDetail />} />
        {/* New canonical SEO routes — root-level city slug ("/tampa-fl")
            and category-prefixed slug ("/dog-parks/tampa-fl"). Placed
            LAST so reserved routes above (search/login/blog/profile/...)
            are matched first. CityPage validates the slug against the
            registry + RESERVED_SLUGS set and redirects to /search on
            misses, so accidental hits never render a broken page. */}
        <Route path="/:categorySlug/:citySlug" element={<CityPage />} />
        <Route path="/:citySlug" element={<CityPage />} />
      </Routes>
      <Footer />
      <InstallPrompt />
    </>
  );
};

function App() {
  useEffect(() => {
    startKeepalive();
  }, []);

  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppRouter />
          <Toaster
            position="top-center"
            toastOptions={{
              className: "!font-body !bg-white !text-brand-ink !border-brand-border-warm",
            }}
          />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
